import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Copy, Heart, MessageCircle, Users, CheckCircle, Zap, Bot, Database, FileText } from "lucide-react";
import { getComplexityColor } from "@/lib/data.ts";
import { toast, toastMessages } from "shared-web/lib/toast";

export function WorkflowCard({ workflow, onPreview, onClone }: { workflow: any; onPreview: (w: any) => void; onClone: (w: any) => void }) {
  // Map category to icon since backend doesn't store React components
  const getIconForCategory = (category: string) => {
    switch (category) {
      case 'Document Processing': return FileText;
      case 'Data Processing': return Database;
      case 'AI/ML': return Bot;
      case 'Productivity': return Zap;
      default: return Zap;
    }
  };
  
  const IconComponent = workflow.icon || getIconForCategory(workflow.category);

  const handleSave = () => {
    toast.success(toastMessages.workflowAdded);
  };

  const handleClone = () => {
    onClone(workflow);
    toast.success('Workflow cloned to your workspace', 'You can now edit and customize it');
  };

  return (
    <Card
      key={workflow.id}
      className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 cursor-pointer"
      onClick={() => onPreview(workflow)}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <IconComponent className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                <Link
                  to={`/gallery/workflow/${workflow.id}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {workflow.title || workflow.name}
                </Link>
              </CardTitle>
              <p className="text-sm text-slate-500 dark:text-slate-400">{workflow.category}</p>
            </div>
          </div>
          <Badge className={getComplexityColor(workflow.complexity)}>{workflow.complexity}</Badge>
        </div>

        <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
          <Avatar className="w-6 h-6">
            <AvatarImage src={workflow.author.avatar || "/placeholder.svg"} alt={workflow.author.name} />
            <AvatarFallback className="text-xs">
              {workflow.author.name
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm text-slate-600 dark:text-slate-400">by {workflow.author.name}</span>
          {workflow.author.verified && <CheckCircle className="w-4 h-4 text-blue-500" />}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="aspect-video bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden">
          <img
            src={workflow.preview || "/placeholder.svg"}
            alt={`${workflow.title || workflow.name} preview`}
            className="w-full h-full object-cover"
          />
        </div>

        <CardDescription className="text-slate-600 dark:text-slate-300 line-clamp-2">
          {workflow.description}
        </CardDescription>

        <div className="flex flex-wrap gap-1">
          {workflow.tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>

        <div className="flex items-center justify-between text-sm text-slate-500 dark:text-slate-400">
          <div className="flex items-center space-x-4">
            <Link
              to={`/gallery/workflow/${workflow.id}#comments`}
              className="flex items-center hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <MessageCircle className="w-4 h-4 mr-1" />
              {workflow.comments?.length || 0} comments
            </Link>
            <span className="flex items-center">
              <Users className="w-4 h-4 mr-1" />
              {workflow.uses.toLocaleString()} uses
            </span>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 bg-transparent"
            onClick={(e) => {
              e.stopPropagation();
              handleSave();
            }}
          >
            <Heart className="w-4 h-4 mr-2" />
            Save
          </Button>
          <Button
            size="sm"
            className="flex-1 bg-blue-600 hover:bg-blue-700"
            onClick={(e) => {
              e.stopPropagation();
              handleClone();
            }}
          >
            <Copy className="w-4 h-4 mr-2" />
            Clone
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
